import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid2,
  Typography,
  TextField,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { DropzoneUploadLargFileField } from '../../shared/components/formfields/DropzoneUploadLargFileField';
import { postActivityAddLsm } from '../../assets/nodeApi/postApi/postAdminApi';
import { postUpdateLms } from '../../assets/nodeApi/putApi/putAdminApi';
import { useGetLmsUpdateContentById, useGetCourses } from '../../assets/nodeApi/getApi/getAdminApi';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { FormSkeleton } from '../../shared/utils/SkeletonCom';
import { Grid_Gap, Input_Width_LG } from '../../shared/constants';
import { useLmsDropdownOptions } from '../addlms/option/LsmOption';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import { TextFormField } from '../../shared/components/formfields/TextFormField';

import theme from '../../theme';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '14px',
    background: theme.palette.background.paper,
    '& fieldset': { borderColor: theme.palette.grey[100] },
    '&:hover fieldset': { borderColor: theme.palette.blue.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.blue.main },
  },
  '& .MuiInputLabel-root': { fontSize: '13px', color: theme.palette.secondary.light },
  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.blue.main },
};

const labelSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  mb: '4px',
  display: 'block',
};

const Req = () => <span style={{ color: theme.palette.error.main, marginLeft: 2 }}>*</span>;

// AutoCompleteField is used as Field component instead of LabelledSelect.

// Removed FileUploadButton
// ─────────────────────────────────────────────
// Section Card  (Activity / Paragraph wrapper)
// ─────────────────────────────────────────────
const SectionCard = ({ title, onDelete, children, borderColor = theme.palette.blue.light }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      p: '20px 24px',
      mb: 2,
      background: theme.palette.primary.main,
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '15px', color: theme.palette.text.primary }}>{title}</Typography>
      {onDelete && (
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{ color: theme.palette.error.main, '&:hover': { background: '#FEF2F2' } }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
    {children}
  </Paper>
);

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const Activelmsadd = () => {
  const navigate = useNavigate();
  const { lmsId } = useParams();
  const location = useLocation();
  const { eventId: stateEventId, gradeId: stateGradeId, subjectId: stateSubjectId } = location.state || {};

  const { data: lmsFullContent, isLoading } = useGetLmsUpdateContentById(lmsId);
  const { showToaster } = useToaster();

  const [selectedCourseId, setSelectedCourseId] = useState(stateEventId || '');
  const [selectedMilestone, setSelectedMilestone] = useState({});
  const [selectedSprint, setSelectedSprint] = useState({});

  const {
    lmsGrade,
    lmsSubject,
    lmsMilestone, lmsSession1, lmsSprint,
  } = useLmsDropdownOptions({
    courseId: selectedCourseId,
    milestoneFilter: selectedCourseId,
    sprintFilter: selectedMilestone?.id,
    session1Filter: selectedSprint?.id,
    gradeFilter: { status: true },
    boardFilter: { status: true },
  });

  const { data: coursesData, isLoading: coursesLoading } = useGetCourses();
  const courseOptions = (coursesData?.data || []).map((c) => ({
    id: String(c.id),
    value: String(c.id),
    label: c.title,
  }));

  const gradeOptions = (lmsGrade.options || []).map((o) => ({
    id: String(o.id),
    value: String(o.id),
    label: o.label,
  }));

  const subjectOptions = (lmsSubject.options || []).map((o) => ({
    id: String(o.id),
    value: String(o.id),
    label: o.label,
  }));

  const milestoneOptions = (lmsMilestone.options || []).map((o) => ({
    id: String(o.id),
    value: String(o.id),
    label: o.label,
  }));

  const sprintOptions = (lmsSprint.options || []).map((o) => ({
    id: String(o.id),
    value: String(o.id),
    label: o.label,
  }));

  const sessionOptions = (lmsSession1.options || []).map((o) => ({
    id: String(o.id),
    value: String(o.id),
    label: o.label,
  }));

  const formikRef = useRef();

  // ── Validation ────────────────────────────
  const validationSchema = Yup.object().shape({
    eventId: Yup.mixed().required('Course is required').test('not-empty', 'Course is required', (v) => v !== '' && v != null),
    gradeId: Yup.mixed().required('Grade is required').test('not-empty', 'Grade is required', (v) => v !== '' && v != null),
    subjectId: Yup.mixed().required('Subject is required').test('not-empty', 'Subject is required', (v) => v !== '' && v != null),
    sessionId: Yup.mixed().required('Topic is required').test('not-empty', 'Topic is required', (v) => v !== '' && v != null),
    subtopics: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().required('Activity name is required'),
        description: Yup.string().trim().required('Description is required'),
        age: Yup.string().trim().required('Age is required'),
        materialsRequired: Yup.string().trim().required('Materials Required is required'),
        duration: Yup.string().trim().required('Duration is required'),
      })
    ).min(1, 'At least one activity is required'),
  });

  // ── Initial values ────────────────────────
  const initialValues = {
    eventId: lmsFullContent?.eventId ? String(lmsFullContent.eventId) : (stateEventId ? String(stateEventId) : ''),
    gradeId: lmsFullContent?.mapping?.grade?.id ? String(lmsFullContent.mapping.grade.id) : (stateGradeId ? String(stateGradeId) : ''),
    subjectId: lmsFullContent?.mapping?.subject?.id ? String(lmsFullContent.mapping.subject.id) : (lmsFullContent?.mapping?.subjectId ? String(lmsFullContent.mapping.subjectId) : (stateSubjectId ? String(stateSubjectId) : '')),
    milestoneId: lmsFullContent?.mapping?.milestoneId ? String(lmsFullContent.mapping.milestoneId) : '',
    sprintId: lmsFullContent?.mapping?.sprintId ? String(lmsFullContent.mapping.sprintId) : '',
    sessionId: lmsFullContent?.mapping?.sessionId ? String(lmsFullContent.mapping.sessionId) : '',
    subtopics: lmsFullContent?.subtopics?.length > 0 ? lmsFullContent.subtopics.map(sub => ({
      ...sub,
      age: sub.age || '',
      videoUrls: sub.videoUrls?.length > 0 ? sub.videoUrls : [''],
      files: sub.files?.length > 0 ? sub.files : [null],
    })) : [{
      name: '', description: '', age: '', materialsRequired: '', duration: '',
      videoUrls: [''],
      files: [null],
    }],
  };

  // ── Mutations ─────────────────────────────
  const mutationAddLMS = useMutation({ mutationFn: postActivityAddLsm });
  const mutationUpdateLMS = useMutation({
    mutationFn: ({ formData, id }) => postUpdateLms(formData, id),
  });
  const isPending = mutationAddLMS.isPending || mutationUpdateLMS.isPending;

  // ── Submit ────────────────────────────────
  const handleSubmit = (values, { resetForm }) => {
    const formData = new FormData();

    // Required top-level fields
    formData.append('eventId', String(values.eventId));
    formData.append('gradeId', String(values.gradeId));
    formData.append('subject_id', String(values.subjectId));
    formData.append('subjectId', String(values.subjectId));
    formData.append('sessionId', String(values.sessionId));

    // Build the activity JSON array (tempId is 1-based index as string)
    const activityPayload = values.subtopics.map((sub, idx) => ({
      tempId: String(idx + 1),
      name: sub.name,
      description: sub.description,
      age: sub.age,
      material_required: sub.materialsRequired,
      duration: sub.duration,
      video_urls: (sub.videoUrls || []).filter(url => typeof url === 'string' && url.trim() !== ''),
    }));

    formData.append('activity', JSON.stringify(activityPayload));

    // Append files per activity using activity_files_{tempId} key
    values.subtopics.forEach((sub, idx) => {
      const tempId = String(idx + 1);
      (sub.files || []).forEach((fileItem) => {
        if (fileItem instanceof File) {
          formData.append(`activity_files_${tempId}`, fileItem);
        }
      });
    });

    if (lmsId) {
      mutationUpdateLMS.mutate({ formData, id: lmsId }, {
        onSuccess: () => showToaster('LMS Updated successfully!', '', 'success'),
        onError: (err) => showToaster(err?.response?.data?.message || 'Failed to Update LMS', '', 'error'),
      });
    } else {
      mutationAddLMS.mutate(formData, {
        onSuccess: () => { showToaster('LMS Added successfully!', '', 'success'); resetForm(); },
        onError: (err) => showToaster(err?.response?.data?.message || 'Failed to Add LMS', '', 'error'),
      });
    }
  };

  useEffect(() => {
    if (lmsFullContent) {
      setSelectedCourseId(lmsFullContent?.eventId);
    }
  }, [lmsId, isLoading, lmsFullContent]);

  // ── Loading skeleton ──────────────────────
  if (isLoading) {
    return (
      <Grid2 container spacing={Grid_Gap}>
        {[...Array(12)].map((_, i) => (
          <Grid2 size={Input_Width_LG} key={i}><FormSkeleton height={40} /></Grid2>
        ))}
      </Grid2>
    );
  }

  // ── Render ────────────────────────────────
  return (
    <Box sx={{
      fontFamily: "'Poppins', sans-serif",
      background: theme.palette.background.paper,
      borderRadius: '16px',
      border: `1px solid ${theme.palette.grey[100]}`,
      p: { xs: '20px', md: '32px' },
      mx: 'auto',
      '& *': { fontFamily: "'Poppins', sans-serif !important" }
    }}>
      {/* Header */}
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.palette.text.primary, mb: '4px' }}>
        {lmsId ? 'Update Course' : 'Adding New Courses'}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: theme.palette.secondary.light, mb: '20px' }}>
        {lmsId ? 'Update the details below' : 'Fill the details to add new course'}
      </Typography>
      <Divider sx={{ mb: '24px', borderColor: theme.palette.grey[100] }} />

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, resetForm, setFieldValue, errors, touched }) => (
          <Form>
            {/* ── Top dropdowns (2-column grid) ── */}
            <Grid2 container spacing="16px" style={{ marginBottom: '24px' }}>

              {/* Course Name */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="eventId"
                  component={AutoCompleteField}
                  label="Course Name"
                  isRequired
                  options={courseOptions}
                  loading={coursesLoading}
                  disabled={!!lmsId || !!stateEventId}
                  onValueChange={(v) => {
                    setSelectedCourseId(v?.id || null);
                    setSelectedMilestone({});
                    setSelectedSprint({});
                    setFieldValue('milestoneId', '');
                    setFieldValue('sprintId', '');
                    setFieldValue('sessionId', '');
                  }}
                  onClear={() => {
                    setSelectedCourseId(null);
                    setSelectedMilestone({});
                    setSelectedSprint({});
                    setFieldValue('milestoneId', '');
                    setFieldValue('sprintId', '');
                    setFieldValue('sessionId', '');
                  }}
                />
              </Grid2>

              {/* Subject */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="subjectId"
                  component={AutoCompleteField}
                  label="Subject"
                  isRequired
                  options={subjectOptions}
                  loading={lmsSubject.isLoading}
                  disabled={!!lmsId || !!stateSubjectId}
                />
              </Grid2>

              {/* Grade */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="gradeId"
                  component={AutoCompleteField}
                  label="Grade"
                  isRequired
                  options={gradeOptions}
                  loading={lmsGrade.isLoading}
                  disabled={!!lmsId || !!stateGradeId}
                />
              </Grid2>

              {/* Milestone */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="milestoneId"
                  component={AutoCompleteField}
                  label="Milestone"
                  isRequired
                  options={milestoneOptions}
                  loading={lmsMilestone.isLoading}
                  onValueChange={(v) => {
                    setSelectedMilestone(v || {});
                    setSelectedSprint({});
                    setFieldValue('sprintId', '');
                    setFieldValue('sessionId', '');
                  }}
                  onClear={() => {
                    setSelectedMilestone({});
                    setSelectedSprint({});
                    setFieldValue('sprintId', '');
                    setFieldValue('sessionId', '');
                  }}
                />
              </Grid2>

              {/* Sprint */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="sprintId"
                  component={AutoCompleteField}
                  label="Sprint"
                  isRequired
                  options={sprintOptions}
                  loading={lmsSprint.isLoading}
                  onValueChange={(v) => {
                    setSelectedSprint(v || {});
                    setFieldValue('sessionId', '');
                  }}
                  onClear={() => {
                    setSelectedSprint({});
                    setFieldValue('sessionId', '');
                  }}
                />
              </Grid2>

              {/* Topic */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Field
                  name="sessionId"
                  component={AutoCompleteField}
                  label="Topic"
                  isRequired
                  options={sessionOptions}
                  loading={lmsSession1.isLoading}
                />
              </Grid2>
            </Grid2>

            {/* ── Activities (subtopics) ── */}
            <FieldArray name="subtopics">
              {({ push: pushActivity, remove: removeActivity }) => (
                <>
                  {values.subtopics.map((activity, aIdx) => (
                    <SectionCard
                      key={aIdx}
                      title="Activity"
                      onDelete={values.subtopics.length > 1 ? () => removeActivity(aIdx) : undefined}
                    >
                      <Grid2 container spacing="16px">

                        {/* Activity name */}
                        <Grid2 size={12}>
                          <Field
                            name={`subtopics[${aIdx}].name`}
                            component={TextFormField}
                            label="Add Activity"
                            isRequired
                            placeholder='Enter Activity'
                          />
                        </Grid2>

                        {/* Description */}
                        <Grid2 size={12}>
                          <Field
                            name={`subtopics[${aIdx}].description`}
                            component={TextFormField}
                            label="Description"
                            isRequired
                            placeholder='Enter Description'
                          />
                        </Grid2>

                        {/* Age group */}
                        <Grid2 size={12}>
                          <Field
                            name={`subtopics[${aIdx}].age`}
                            component={TextFormField}
                            label="Age"
                            isRequired
                            placeholder='Enter Age'
                          />
                        </Grid2>

                        {/* Materials Required */}
                        <Grid2 size={12}>
                          <Field
                            name={`subtopics[${aIdx}].materialsRequired`}
                            component={TextFormField}
                            label="Materials Required"
                            isRequired
                            multiline
                            rows={2}
                            placeholder='Enter Materials Required'
                          />
                        </Grid2>

                        {/* Duration */}
                        <Grid2 size={12}>
                          <Field
                            name={`subtopics[${aIdx}].duration`}
                            component={TextFormField}
                            label="Duration"
                            isRequired
                            placeholder="e.g. 30 mins"
                          />
                        </Grid2>

                        {/* ── Video URL ── */}
                        <Grid2 size={12}>
                          <Typography sx={labelSx}>Video URL</Typography>
                          <FieldArray name={`subtopics[${aIdx}].videoUrls`}>
                            {({ push: pushVideoUrl, remove: removeVideoUrl }) => (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                {activity.videoUrls && activity.videoUrls.length > 0 ? (
                                  activity.videoUrls.map((url, vIdx) => (
                                    <Box key={vIdx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                      <Field
                                        name={`subtopics[${aIdx}].videoUrls[${vIdx}]`}
                                        component={TextFormField}
                                        placeholder="Enter Video URL"
                                      />
                                      {activity.videoUrls.length > 1 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => removeVideoUrl(vIdx)}
                                          sx={{ color: theme.palette.error.main, mt: 2.5 }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))
                                ) : null}
                                <Button
                                  variant="outlined" size="small" startIcon={<AddIcon />}
                                  onClick={() => pushVideoUrl('')}
                                  sx={{
                                    alignSelf: 'flex-start',
                                    borderRadius: '8px', borderColor: theme.palette.blue.main,
                                    color: theme.palette.blue.main, fontSize: '13px', textTransform: 'none',
                                    '&:hover': { background: theme.palette.blue.lighter },
                                  }}
                                >
                                  Add URL
                                </Button>
                              </Box>
                            )}
                          </FieldArray>
                        </Grid2>

                        {/* ── File Upload ── */}
                        <Grid2 size={12}>
                          <Typography sx={labelSx}>File Upload<Req /></Typography>
                          <FieldArray name={`subtopics[${aIdx}].files`}>
                            {({ push: pushFile, remove: removeFile }) => (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {activity.files && activity.files.length > 0 ? (
                                  activity.files.map((fileItem, fIdx) => (
                                    <Box key={fIdx} sx={{ position: 'relative' }}>
                                      <Field
                                        name={`subtopics[${aIdx}].files[${fIdx}]`}
                                        component={DropzoneUploadLargFileField}
                                        label="Upload File"
                                        text_label={fIdx === 0 ? "Images, PDF, DOC, DOCX (max 2 MB)" : ""}
                                        fileDetail={typeof fileItem === 'object' && fileItem?.file_type ? fileItem : undefined}
                                      />
                                      {activity.files.length > 1 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => removeFile(fIdx)}
                                          sx={{ position: 'absolute', top: 0, right: 0, color: theme.palette.error.main }}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))
                                ) : null}
                                <Button
                                  variant="outlined" size="small" startIcon={<AddIcon />}
                                  onClick={() => pushFile(null)}
                                  sx={{
                                    alignSelf: 'flex-start',
                                    borderRadius: '8px', borderColor: theme.palette.blue.main,
                                    color: theme.palette.blue.main, fontSize: '13px', textTransform: 'none',
                                    '&:hover': { background: theme.palette.blue.lighter },
                                  }}
                                >
                                  Add File
                                </Button>
                              </Box>
                            )}
                          </FieldArray>
                        </Grid2>
                      </Grid2>

                      {/* Add Activity (inside last card) */}
                      <Divider sx={{ my: '16px', borderColor: theme.palette.grey[100] }} />
                      <Button
                        variant="outlined" startIcon={<AddIcon />} fullWidth
                        onClick={() =>
                          pushActivity({
                            name: '', description: '', age: '', materialsRequired: '', duration: '',
                            videoUrls: [''],
                            files: [null],
                          })
                        }
                        sx={{
                          borderRadius: '8px', borderColor: theme.palette.blue.main,
                          color: theme.palette.blue.main, fontSize: '13px', textTransform: 'none',
                          py: '9px', '&:hover': { background: theme.palette.blue.lighter },
                        }}
                      >
                        Add Activity
                      </Button>
                    </SectionCard>
                  ))}
                </>
              )}
            </FieldArray>

            <Divider sx={{ mb: '24px', borderColor: theme.palette.grey[100] }} />

            {/* Footer buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => resetForm()}
                sx={{
                  borderRadius: '10px', borderColor: theme.palette.grey[100], color: theme.palette.text.primary,
                  fontSize: '14px', textTransform: 'none', px: '28px', py: '10px',
                  '&:hover': { borderColor: theme.palette.text.primary },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained" type="submit" disabled={isPending}
                sx={{
                  borderRadius: '10px', background: theme.palette.blue.main,
                  fontSize: '14px', textTransform: 'none', color: '#FFF',
                  px: '32px', py: '10px', boxShadow: 'none',
                  '&:hover': { background: '#1D4ED8', boxShadow: 'none' },
                }}
              >
                {isPending
                  ? (lmsId ? 'Updating...' : 'Submitting...')
                  : (lmsId ? 'Update' : 'Submit')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default Activelmsadd;